/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * RFC 3161 TimeStampToken validation for C2PA. A COSE_Sign1 may carry a
 * countersigned timestamp in its unprotected `sigTst2` (V2, over the COSE
 * signature) or `sigTst` (V1, over the claim data) header. c2pa-rs honors the
 * timestamp's `genTime` as the certificate-validity reference time only when the
 * timestamp itself validates AND its TSA chain is trusted (governed by the
 * independent `verify_timestamp_trust` setting, which defaults on). This mirrors
 * `c2pa-crypto`'s `verify_time_stamp`:
 *  1. the messageImprint covers the COSE counter-signature data,
 *  2. the signed message-digest attribute matches the embedded TSTInfo,
 *  3. the TSA's CMS signature over the signed attributes verifies,
 *  4. the TSA signing cert is valid at `genTime`,
 *  5. the TSA chain reaches a trust anchor (only possible with a trust store).
 * With no store (trust verification off, no anchors supplied) step 5 fails, so
 * the timestamp is not trusted and the certificate is checked against `now`.
 */

import { encodeArray, encodeBytes, encodeText } from '../cbor/encode'
import { readTlv, readChildren, content, tlvBytes, decodeOid, type Tlv } from './der'
import { parseCertificate, type CertInfo } from './parseCert'
import { parseGeneralizedTime } from './parseTimestamp'
import { evaluateTrust, ecdsaDerToRaw, type TrustStore } from './trust'
import type { CoseSign1 } from '../cose/decodeSign1'

const ID_SIGNED_DATA = '1.2.840.113549.1.7.2'
const ID_CT_TSTINFO = '1.2.840.113549.1.9.16.1.4'
const OID_MESSAGE_DIGEST = '1.2.840.113549.1.9.4'
/** id-kp-timeStamping — the only EKU permitted on an RFC 3161 TSA signing cert. */
const TIMESTAMP_EKU_OID = '1.3.6.1.5.5.7.3.8'

/**
 * Derives the TSA trust store from the caller's store (§14.4.2 / §14.4.3): TSA
 * trust is anchor-only and gated on the id-kp-timeStamping EKU — the allow-listed
 * end-entities (the private credential store) "shall not apply to validating
 * time-stamps" (l.2353), so they are dropped. Otherwise an allow-listed C2PA
 * signing cert could be accepted as a trusted TSA and used to forge trusted
 * timestamps. Exported for unit testing.
 */
export function tsaTrustStore (store: TrustStore): TrustStore {
    return { ...store, allowedEku: new Set([TIMESTAMP_EKU_OID]), allowedEndEntities: new Set<string>() }
}

const HASH_FOR_OID: Record<string, string> = {
    '2.16.840.1.101.3.4.2.1': 'SHA-256',
    '2.16.840.1.101.3.4.2.2': 'SHA-384',
    '2.16.840.1.101.3.4.2.3': 'SHA-512'
}
const ECDSA_SIZE: Record<string, number> = { 'SHA-256': 32, 'SHA-384': 48, 'SHA-512': 66 }
const ECDSA_CURVE: Record<string, string> = { 'SHA-256': 'P-256', 'SHA-384': 'P-384', 'SHA-512': 'P-521' }
/** EC named-curve OID → WebCrypto curve name + raw r||s component size (TS-4). */
const EC_CURVE_BY_OID: Record<string, { name: string, size: number }> = {
    '1.2.840.10045.3.1.7': { name: 'P-256', size: 32 },
    '1.3.132.0.34': { name: 'P-384', size: 48 },
    '1.3.132.0.35': { name: 'P-521', size: 66 }
}

const oidOf = (u8: Uint8Array, t: Tlv): string => decodeOid(content(u8, t))
const hex = (b: Uint8Array): string => Array.from(b, x => x.toString(16).padStart(2, '0')).join('')

async function digest (alg: string, data: Uint8Array): Promise<Uint8Array> {
    return new Uint8Array(await crypto.subtle.digest(alg, data as unknown as ArrayBuffer))
}

/** DER short/long-form length prefix for a known content length. */
function derLen (n: number): number[] {
    if (n < 0x80) return [n]
    if (n < 0x100) return [0x81, n]
    if (n < 0x10000) return [0x82, n >> 8, n & 0xff]
    return [0x83, (n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

/** The COSE `Sig_structure` for a CounterSignature over `payload`. */
function counterSignData (protectedRaw: Uint8Array, payload: Uint8Array): Uint8Array {
    return encodeArray([encodeText('CounterSignature'), encodeBytes(protectedRaw), encodeBytes(new Uint8Array(0)), encodeBytes(payload)])
}

/** Locates the CMS SignedData SEQUENCE inside a bare TimeStampToken or a TimeStampResp. */
function findSignedData (u8: Uint8Array): Tlv {
    const seq = readTlv(u8, 0)
    const kids = readChildren(u8, seq.contentStart, seq.contentEnd)
    let ci = seq
    if (!(kids[0]?.tag === 0x06 && oidOf(u8, kids[0]) === ID_SIGNED_DATA)) {
        for (const k of kids) {
            if (k.tag === 0x30) {
                const sub = readChildren(u8, k.contentStart, k.contentEnd)
                if (sub[0]?.tag === 0x06 && oidOf(u8, sub[0]) === ID_SIGNED_DATA) { ci = k; break }
            }
        }
    }
    const ciKids = readChildren(u8, ci.contentStart, ci.contentEnd)
    return readChildren(u8, ciKids[1].contentStart, ciKids[1].contentEnd)[0]
}

interface ParsedToken {
    /** The TSTInfo DER bytes (encapContentInfo eContent), i.e. the digested content. */
    tstInfo: Uint8Array
    /** messageImprint hash algorithm (WebCrypto name) + hash. */
    imprintAlg: string
    imprintHash: Uint8Array
    /** SignerInfo digest algorithm (WebCrypto name). */
    digestAlg: string
    /** SignerInfo signatureAlgorithm OID. */
    sigAlgOid: string
    /** DER of the SignedAttributes re-tagged as SET OF (the TSA-signed bytes), or null. */
    signedAttrsDer: Uint8Array | null
    /** The signed message-digest attribute value, or null. */
    messageDigestAttr: Uint8Array | null
    /** The SignerInfo signature value. */
    signature: Uint8Array
    /** Embedded certificates (DER). */
    certDers: Uint8Array[]
}

/** Parses the fields of a TimeStampToken needed to validate it. */
function parseToken (token: Uint8Array): ParsedToken | null {
    const sd = findSignedData(token)
    const sdKids = readChildren(token, sd.contentStart, sd.contentEnd)

    // encapContentInfo: SEQUENCE { contentType id-ct-TSTInfo, [0] EXPLICIT OCTET STRING(TSTInfo) }
    const encap = sdKids.find(k => k.tag === 0x30)
    if (!encap) return null
    const encapKids = readChildren(token, encap.contentStart, encap.contentEnd)
    if (oidOf(token, encapKids[0]) !== ID_CT_TSTINFO) return null
    const octet = readChildren(token, encapKids[1].contentStart, encapKids[1].contentEnd)[0]
    const tstInfo = content(token, octet)

    // messageImprint is the first SEQUENCE inside TSTInfo.
    const tst = readTlv(token, octet.contentStart)
    const tstKids = readChildren(token, tst.contentStart, tst.contentEnd)
    const mi = tstKids.find(k => k.tag === 0x30)
    if (!mi) return null
    const miKids = readChildren(token, mi.contentStart, mi.contentEnd)
    const imprintOid = oidOf(token, readChildren(token, miKids[0].contentStart, miKids[0].contentEnd)[0])
    const imprintAlg = HASH_FOR_OID[imprintOid]
    if (!imprintAlg) return null
    const imprintHash = content(token, miKids[1])

    // certificates: [0] IMPLICIT SET OF CertificateChoices.
    const certsCtx = sdKids.find(k => k.tag === 0xa0)
    const certDers = certsCtx ? readChildren(token, certsCtx.contentStart, certsCtx.contentEnd).filter(c => c.tag === 0x30).map(c => tlvBytes(token, c)) : []

    // signerInfos: the last SET OF SignerInfo.
    const siSet = sdKids.filter(k => k.tag === 0x31).pop()
    if (!siSet) return null
    const si = readChildren(token, siSet.contentStart, siSet.contentEnd)[0]
    const siKids = readChildren(token, si.contentStart, si.contentEnd)
    // SignerInfo SEQUENCEs: [?sid(IssuerAndSerial), digestAlgorithm, signatureAlgorithm].
    // digestAlgorithm is always second-to-last SEQUENCE; signatureAlgorithm is last.
    const seqs = siKids.filter(k => k.tag === 0x30)
    if (seqs.length < 2) return null
    const digestAlg = HASH_FOR_OID[oidOf(token, readChildren(token, seqs[seqs.length - 2].contentStart, seqs[seqs.length - 2].contentEnd)[0])]
    const sigAlgOid = oidOf(token, readChildren(token, seqs[seqs.length - 1].contentStart, seqs[seqs.length - 1].contentEnd)[0])
    const signature = content(token, siKids.filter(k => k.tag === 0x04).pop()!)

    // signedAttrs [0] IMPLICIT: the TSA signs the DER of this re-tagged as SET OF (0x31).
    const signedAttrsCtx = siKids.find(k => k.tag === 0xa0)
    let signedAttrsDer: Uint8Array | null = null
    let messageDigestAttr: Uint8Array | null = null
    if (signedAttrsCtx) {
        const inner = token.subarray(signedAttrsCtx.contentStart, signedAttrsCtx.contentEnd)
        signedAttrsDer = new Uint8Array([0x31, ...derLen(inner.length), ...inner])
        for (const attr of readChildren(token, signedAttrsCtx.contentStart, signedAttrsCtx.contentEnd)) {
            const ak = readChildren(token, attr.contentStart, attr.contentEnd)
            if (oidOf(token, ak[0]) === OID_MESSAGE_DIGEST) {
                const v = readChildren(token, ak[1].contentStart, ak[1].contentEnd)[0]
                messageDigestAttr = content(token, v)
            }
        }
    }

    if (!digestAlg) return null
    return { tstInfo, imprintAlg, imprintHash, digestAlg, sigAlgOid, signedAttrsDer, messageDigestAttr, signature, certDers }
}

const PSS_SALT: Record<string, number> = { 'SHA-256': 32, 'SHA-384': 48, 'SHA-512': 64 }

/** Verifies the TSA's CMS signature (`signed bytes`) against a candidate cert; returns the cert that verifies. */
async function verifyTsaSignature (parsed: ParsedToken): Promise<CertInfo | null> {
    // Per RFC 5652 §5.4: when signedAttrs is present the TSA signs their DER (re-tagged
    // as SET OF); when absent it signs the eContent (the TSTInfo) directly (§15.8.2).
    const signedBytes = parsed.signedAttrsDer ?? parsed.tstInfo
    const isEcdsa = parsed.sigAlgOid.startsWith('1.2.840.10045')
    const isPss = parsed.sigAlgOid === '1.2.840.113549.1.1.10'
    const verifyParams: EcdsaParams | RsaPssParams | Algorithm = isEcdsa
        ? { name: 'ECDSA', hash: parsed.digestAlg }
        : isPss ? { name: 'RSA-PSS', saltLength: PSS_SALT[parsed.digestAlg] ?? 32 } : { name: 'RSASSA-PKCS1-v1_5' }
    for (const der of parsed.certDers) {
        let cert: CertInfo
        try { cert = parseCertificate(der) } catch { continue }
        // TS-4: for ECDSA, take the curve from the TSA cert's actual key, not one
        // inferred from the digest alg (a P-256 key signing with SHA-384 would else
        // be false-rejected). Fall back to the digest-derived curve if the cert has none.
        const curve = isEcdsa ? (EC_CURVE_BY_OID[cert.ecCurveOid ?? ''] ?? { name: ECDSA_CURVE[parsed.digestAlg] ?? 'P-256', size: ECDSA_SIZE[parsed.digestAlg] ?? 32 }) : null
        const importParams: EcKeyImportParams | RsaHashedImportParams = isEcdsa
            ? { name: 'ECDSA', namedCurve: curve!.name }
            : { name: isPss ? 'RSA-PSS' : 'RSASSA-PKCS1-v1_5', hash: parsed.digestAlg }
        try {
            const key = await crypto.subtle.importKey('spki', cert.spki as unknown as ArrayBuffer, importParams, false, ['verify'])
            let sig = parsed.signature
            if (isEcdsa) sig = ecdsaDerToRaw(sig, curve!.size)
            if (await crypto.subtle.verify(verifyParams, key, sig as unknown as ArrayBuffer, signedBytes as unknown as ArrayBuffer)) return cert
        } catch { /* try next cert */ }
    }
    return null
}

/** Orders the embedded certificates leaf→root starting from the verified TSA signer cert. */
function orderChain (certDers: Uint8Array[], signer: CertInfo): Uint8Array[] {
    const parsed = certDers.map(der => { try { return { der, info: parseCertificate(der) } } catch { return null } }).filter((c): c is { der: Uint8Array, info: CertInfo } => c !== null)
    const start = parsed.find(c => hex(c.info.subjectDer) === hex(signer.subjectDer))
    if (!start) return []
    const ordered = [start.der]
    const used = new Set([hex(start.info.subjectDer)])
    let cur = start.info
    for (let i = 0; i < parsed.length; i++) {
        const next = parsed.find(c => !used.has(hex(c.info.subjectDer)) && hex(c.info.subjectDer) === hex(cur.issuerDer))
        if (!next) break
        ordered.push(next.der); used.add(hex(next.info.subjectDer)); cur = next.info
    }
    return ordered
}

export interface TimestampResult {
    /** The TSTInfo genTime (ISO-8601), or null if no/invalid timestamp. */
    genTime: string | null
    /**
     * Whether the token is internally VALID independent of trust: the messageImprint
     * covers the COSE counter-signature, the signed message-digest is self-consistent,
     * the TSA's CMS signature verifies under an embedded cert, and that cert is valid
     * at genTime. A forged/garbage token is `validated:false` (TS-1). Trust (chain to
     * an anchor) is a separate, stronger condition.
     */
    validated: boolean
    /** Whether the timestamp validated AND its TSA chain is trusted to an anchor. */
    trusted: boolean
}

/**
 * Validates the COSE_Sign1's RFC 3161 timestamp and decides whether it is
 * trusted. `claimRaw` is the claim CBOR (the V1 counter-signed data). `store` is
 * the TSA trust store (the C2PA trust anchors); when null the timestamp can be
 * validated but never trusted, so the caller falls back to `now`.
 */
export async function validateTimestamp (sign1: CoseSign1, claimRaw: Uint8Array, store: TrustStore | null): Promise<TimestampResult> {
    const v2 = sign1.unprotectedHeader.get('sigTst2')
    const v1 = sign1.unprotectedHeader.get('sigTst')
    const header = v2 ?? v1
    const tokens = (header as { tstTokens?: Array<{ val?: unknown }> } | undefined)?.tstTokens
    // §10.3.2.5: "A manifest shall contain only one time-stamp." A container carrying
    // more than one tstToken is malformed → ignore the timestamp (cert checked vs now).
    if (tokens && tokens.length > 1) return { genTime: null, validated: false, trusted: false }
    const token = tokens?.[0]?.val
    if (!(token instanceof Uint8Array)) return { genTime: null, validated: false, trusted: false }

    let parsed: ParsedToken | null
    try { parsed = parseToken(token) } catch { parsed = null }
    if (!parsed) return { genTime: null, validated: false, trusted: false }

    // genTime from the embedded TSTInfo.
    const tstKids = readChildren(parsed.tstInfo, readTlv(parsed.tstInfo, 0).contentStart, readTlv(parsed.tstInfo, 0).contentEnd)
    const genTimeTlv = tstKids.find(k => k.tag === 0x18) // GeneralizedTime
    let genTime: string | null = null
    if (genTimeTlv) {
        // Robust GeneralizedTime parsing (§15.8.2): a malformed genTime yields null,
        // so the timestamp is ignored and the cert is checked against `now` rather
        // than against a garbage reference time.
        genTime = parseGeneralizedTime(new TextDecoder().decode(content(parsed.tstInfo, genTimeTlv)))
    }
    if (!genTime) return { genTime: null, validated: false, trusted: false }

    try {
        // (1) messageImprint covers the COSE counter-signature data. These validation
        // steps (1–4) run WITHOUT a trust store so `validated` can be reported for the
        // ingredient inherited-trust path (TS-1) — a forged/garbage token fails here.
        const payload = v2 ? encodeBytes(sign1.signature) : claimRaw
        const tbs = counterSignData(sign1.protectedRaw, payload)
        if (hex(await digest(parsed.imprintAlg, tbs)) !== hex(parsed.imprintHash)) return { genTime, validated: false, trusted: false }

        // (2) signed message-digest attribute matches the embedded TSTInfo.
        if (parsed.signedAttrsDer) {
            if (!parsed.messageDigestAttr) return { genTime, validated: false, trusted: false }
            if (hex(await digest(parsed.digestAlg, parsed.tstInfo)) !== hex(parsed.messageDigestAttr)) return { genTime, validated: false, trusted: false }
        }

        // (3) the TSA's CMS signature verifies (identifying the signer cert).
        const signer = await verifyTsaSignature(parsed)
        if (!signer) return { genTime, validated: false, trusted: false }

        // (4) the TSA signing cert is valid at genTime.
        if (!(signer.notBefore <= genTime && genTime <= signer.notAfter)) return { genTime, validated: false, trusted: false }

        // The token is now internally valid and signed by its embedded TSA cert.
        const validated = true

        // (5) the TSA chain reaches a trust anchor. Requires a store; the TSA leaf must
        // carry the id-kp-timeStamping EKU specifically (c2pa-rs clears the EKU set and
        // adds only the timestamp OID for this check), so constrain the store's EKU set
        // rather than accepting any allowed signer EKU.
        if (!store) return { genTime, validated, trusted: false }
        const chain = orderChain(parsed.certDers, signer)
        if (chain.length === 0) return { genTime, validated, trusted: false }
        const trust = await evaluateTrust(chain, tsaTrustStore(store), genTime)
        return { genTime, validated, trusted: trust.trusted }
    } catch {
        return { genTime, validated: false, trusted: false }
    }
}
